import { useCallback, useMemo, useState, useEffect } from "react";
import type { Firestore } from "firebase/firestore";
import { collection, getDocs, limit, query, where, orderBy, startAt, endAt, type QueryConstraint } from "firebase/firestore";
import { parseLooseDateToISO } from "../lib/dateUtils";
import type { AnchorRect } from "../components/FilterPopover";

type Params<T extends Record<string, string>> = {
  db: Firestore;
  customerNameById: Record<string, string>;
  initialFilters: T;
  isCustomerPortal: boolean;
  customerKeys: string[];
};

export function useInventoryFilters<T extends Record<string, string>>({
  db,
  customerNameById,
  initialFilters,
  isCustomerPortal,
  customerKeys,
}: Params<T>) {
  const [filters, setFilters] = useState<T>(initialFilters);
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({});
  const [distinctValues, setDistinctValues] = useState<Record<string, string[]>>({});
  const [filterAnchor, setFilterAnchor] = useState<AnchorRect | null>(null);
  const [serialCache, setSerialCache] = useState<{ term: string; list: string[] } | null>(null);

  const filtersActive = useMemo(() => {
    return Object.values(filters).some((v) => (v || "").toString().trim() !== "");
  }, [filters]);

  const queryFilters = useMemo<Record<string, string>>(() => ({ ...filters }), [filters]);

  const createdAtRange = useMemo<{ from: number | null; to: number | null }>(() => {
    const raw = (filters as any)?.createdAt ?? "";
    const txt = raw?.trim?.() ?? raw;
    if (!txt) return { from: null, to: null };

    const startOfDay = (d: Date): number => {
      const copy = new Date(d);
      copy.setHours(0, 0, 0, 0);
      return copy.getTime();
    };
    const endOfDay = (d: Date): number => {
      const copy = new Date(d);
      copy.setHours(23, 59, 59, 999);
      return copy.getTime();
    };

    const normalizedLower = txt.toLowerCase();
    if (normalizedLower === "idag" || normalizedLower === "today") {
      const now = new Date();
      return { from: startOfDay(now), to: endOfDay(now) };
    }
    if (
      normalizedLower === "senaste veckan" ||
      normalizedLower === "senaste 7 dagar" ||
      normalizedLower === "last week" ||
      normalizedLower === "last 7 days"
    ) {
      const now = new Date();
      const from = new Date(now);
      from.setDate(now.getDate() - 6); // inkluderar idag => 7 dagar
      return { from: startOfDay(from), to: endOfDay(now) };
    }

    const toMillis = (s: string): number | null => {
      const iso = parseLooseDateToISO(s);
      if (!iso) return null;
      const t = Date.parse(iso);
      return Number.isNaN(t) ? null : t;
    };

    const normalized = txt.replace(/\s+to\s+/i, "..");
    const parts = normalized.includes("..") ? normalized.split("..").map((p) => p.trim()).filter(Boolean) : [normalized];

    if (parts.length === 2) {
      const start = toMillis(parts[0]);
      const end = toMillis(parts[1]);
      if (!start || !end) return { from: null, to: null };
      return { from: start, to: endOfDay(new Date(end)) };
    }

    const single = toMillis(parts[0]);
    if (!single) return { from: null, to: null };
    return { from: startOfDay(new Date(single)), to: endOfDay(new Date(single)) };
  }, [filters]);

  const loadFilterOptions = useCallback(
    async (key: keyof T) => {
      if (key === "serial") {
        const term = filterSearch.trim();
        // Ingen prefetch av alla serienummer; vänta tills användaren börjar skriva
        if (!term) {
          setFilterOptions((prev) => ({ ...prev, serial: [] }));
          setDistinctValues((prev) => ({ ...prev, serial: [] }));
          return;
        }

        // Återanvänd senaste resultatet om söksträngen är oförändrad
        if (serialCache && serialCache.term === term) {
          setFilterOptions((prev) => ({ ...prev, serial: serialCache.list }));
          setDistinctValues((prev) => ({ ...prev, serial: serialCache.list }));
          return;
        }

        setLoadingFilterOptions(true);
        try {
          const constraints: QueryConstraint[] = [orderBy("serial")];
          if (isCustomerPortal && customerKeys.length > 0) {
            const keys = customerKeys.slice(0, 10);
            constraints.unshift(
              keys.length === 1
                ? where("customerId", "==", keys[0])
                : where("customerId", "in", keys)
            );
          }

          constraints.push(startAt(term), endAt(term + "\uf8ff"));
          constraints.push(limit(50));

          const snap = await getDocs(query(collection(db, "itInventory"), ...constraints));
          const vals = new Set<string>();
          snap.forEach((d) => {
            const data: any = d.data();
            if (data.serial) vals.add(String(data.serial));
          });
          const list = Array.from(vals).filter(Boolean);
          setFilterOptions((prev) => ({ ...prev, serial: list }));
          setDistinctValues((prev) => ({ ...prev, serial: list }));
          setSerialCache({ term, list });
        } catch (e) {
          console.warn("Kunde inte ladda serienummer:", e);
        } finally {
          setLoadingFilterOptions(false);
        }
        return;
      }

      if (filterOptions[key as string]) return;

      if (key === "createdAt") {
        const opts = ["Idag", "Senaste veckan"];
        setFilterOptions((prev) => ({ ...prev, [key as string]: opts }));
        setDistinctValues((prev) => ({ ...prev, [key as string]: opts }));
        return;
      }

      if (key === "chargerIncluded") {
        const opts = ["Ja", "Nej"];
        setFilterOptions((prev) => ({ ...prev, [key as string]: opts }));
        setDistinctValues((prev) => ({ ...prev, [key as string]: opts }));
        return;
      }

      if (isCustomerPortal && customerKeys.length === 0) {
        setFilterOptions((prev) => ({ ...prev, [key as string]: [] }));
        setDistinctValues((prev) => ({ ...prev, [key as string]: [] }));
        return;
      }
      setLoadingFilterOptions(true);
      try {
        const constraints: QueryConstraint[] = [limit(1000)];
        if (isCustomerPortal && customerKeys.length > 0) {
          const keys = customerKeys.slice(0, 10);
          constraints.unshift(
            keys.length === 1
              ? where("customerId", "==", keys[0])
              : where("customerId", "in", keys)
          );
        }
        const snap = await getDocs(query(collection(db, "itInventory"), ...constraints));
        const vals = new Set<string>();
        snap.forEach((d) => {
          const data: any = d.data();
          if (key === "customer") {
            vals.add(data.customer || customerNameById[data.customerId || ""] || "");
          } else if (key === "createdBy") {
            vals.add(data.createdBy || data.initials || "");
          } else {
            vals.add(data[key as string] || "");
          }
        });
        const list = Array.from(vals)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "sv", { sensitivity: "base" }));
        setFilterOptions((prev) => ({ ...prev, [key as string]: list }));
        setDistinctValues((prev) => ({ ...prev, [key as string]: list }));
      } catch (e) {
        console.warn("Kunde inte ladda filtervärden:", e);
      } finally {
        setLoadingFilterOptions(false);
      }
    },
    [customerKeys, customerNameById, db, filterOptions, isCustomerPortal]
  );

  const handleOpenFilter = useCallback(
    (key: keyof T, evt: React.MouseEvent<HTMLButtonElement>) => {
      const rect = evt.currentTarget.getBoundingClientRect();
      setFilterAnchor({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      });
      setOpenFilterKey(openFilterKey === key ? null : (key as string));
      setFilterSearch("");
      void loadFilterOptions(key);
    },
    [loadFilterOptions, openFilterKey]
  );

  useEffect(() => {
    if (openFilterKey !== "serial") return;
    const term = filterSearch.trim();
    if (!term) return;
    const handle = setTimeout(() => {
      void loadFilterOptions("serial" as keyof T);
    }, 300);
    return () => clearTimeout(handle);
  }, [openFilterKey, filterSearch, loadFilterOptions]);

  return {
    filters,
    setFilters,
    filtersActive,
    queryFilters,
    createdAtRange,
    openFilterKey,
    setOpenFilterKey,
    filterSearch,
    setFilterSearch,
    loadingFilterOptions,
    filterOptions,
    distinctValues,
    filterAnchor,
    handleOpenFilter,
    loadFilterOptions,
    setFilterAnchor,
  } as const;
}
